/**
 * Factory for creating MultiColumnDropdown configurations with path-aware builder
 *
 * This factory provides a more readable and maintainable way to define dropdown configurations
 * by splitting them by the first column selection (path) and automatically handling conditions.
 *
 * Features:
 * - Automatic first column condition injection
 * - Path-based configuration splitting
 * - Predefined operator sets and value sets
 * - Simplified column creation helpers
 * - CTA (call-to-action) support for any column
 *
 * Example usage:
 * ```typescript
 * const config = createPathAwareDropdown()
 *   .criteria([
 *     { id: "policy", name: "Policy" },
 *     { id: "account", name: "Account" }
 *   ], "Select Type")
 *   .path("policy", (path) =>
 *     path
 *       .column(pathOperators(OPERATORS.STRING_EXTENDED))
 *       .column(
 *         column
 *           .list("Select Policy", policyItems)
 *           .withCTA(<button>Clear</button>) // CTA on any column
 *       )
 *   )
 *   .path("account", (path) =>
 *     path
 *       .column(pathOperators(OPERATORS.STRING_EXTENDED))
 *   )
 *   .build();
 * ```
 */

import type {
  SteppedMultiColumnDropdownConfig,
  Column,
  ColumnType,
  ListColumn,
  CheckboxColumn,
  SearchColumn,
  EnterValueColumn,
  Item,
} from "@stacklet/ui";

// Predefined operator sets for reusability
export const OPERATORS = {
  STRING_EXTENDED: [
    { id: "is-one-of", name: "Is One Of" },
    { id: "is-not-one-of", name: "Is Not One Of" },
    { id: "equals", name: "Equals" },
    { id: "does-not-equal", name: "Does Not Equal" },
    { id: "contains", name: "Contains" },
    { id: "does-not-contain", name: "Does Not Contain" },
    { id: "starts-with", name: "Starts With" },
    { id: "ends-with", name: "Ends With" },
  ],
  EQUALITY: [
    { id: "equals", name: "Equals" },
    { id: "does-not-equal", name: "Does Not Equal" },
  ],
  BOOLEAN: [
    { id: "is", name: "Is" },
    { id: "is-not", name: "Is Not" },
  ],
  SELECTION: [
    { id: "is-one-of", name: "Is One Of" },
    { id: "is-not-one-of", name: "Is Not One Of" },
  ],
  EQUALITY_AND_SELECTION: [
    { id: "equals", name: "Equals" },
    { id: "does-not-equal", name: "Does Not Equal" },
    { id: "is-one-of", name: "Is One Of" },
    { id: "is-not-one-of", name: "Is Not One Of" },
  ],
};

// Predefined value sets
export const VALUES = {
  EXISTENCE: [
    { id: "empty", name: "Empty" },
    { id: "not-empty", name: "Not Empty" },
    { id: "absent", name: "Absent" },
    { id: "present", name: "Present" },
  ],
};

// Type helpers
type ConditionFn = (value: string[]) => boolean;

// Condition builder helpers
export const conditions = {
  /** Check if value at specific index equals any of the provided values */
  equals:
    (index: number, ...values: string[]): ConditionFn =>
    (value) =>
      values.includes(value[index]),

  /** Check if value at specific index includes any of the provided values */
  includesAny:
    (index: number, ...values: string[]): ConditionFn =>
    (value) =>
      values.some((v) => value[index]?.includes(v)),

  /** Combine multiple conditions with AND logic */
  and:
    (...conditions: ConditionFn[]): ConditionFn =>
    (value) =>
      conditions.every((condition) => condition(value)),

  /** Combine multiple conditions with OR logic */
  or:
    (...conditions: ConditionFn[]): ConditionFn =>
    (value) =>
      conditions.some((condition) => condition(value)),

  /** Negate a condition */
  not:
    (condition: ConditionFn): ConditionFn =>
    (value) =>
      !condition(value),
};

// Column builder configuration type
interface ColumnConfig {
  type: ColumnType;
  title: string;
  items?: Item[];
  placeholder?: string;
  condition?: (value: string[]) => boolean;
  flex?: boolean;
  allowCustomValue?: boolean;
  cta?: React.ReactNode;
}

// Column builder with fluent interface
class ColumnBuilder {
  private config: ColumnConfig;

  constructor(
    type: ColumnType,
    title: string,
    items?: Item[],
    placeholder?: string,
  ) {
    this.config = { type, title };
    if (items) this.config.items = items;
    if (placeholder) this.config.placeholder = placeholder;
  }

  withCondition(condition: ConditionFn): ColumnBuilder {
    this.config.condition = condition;
    return this;
  }

  withFlex(flex = true): ColumnBuilder {
    this.config.flex = flex;
    return this;
  }

  withPlaceholder(placeholder: string): ColumnBuilder {
    this.config.placeholder = placeholder;
    return this;
  }

  allowCustomValue(allow = true): ColumnBuilder {
    this.config.allowCustomValue = allow;
    return this;
  }

  withCTA(cta: React.ReactNode): ColumnBuilder {
    this.config.cta = cta;
    return this;
  }

  build(): Column {
    // Build the specific column type based on the type property
    switch (this.config.type) {
      case "list":
        return {
          type: "list",
          title: this.config.title,
          items: this.config.items || [],
          ...(this.config.condition
            ? { condition: this.config.condition }
            : {}),
          ...(this.config.flex ? { flex: this.config.flex } : {}),
          ...(this.config.cta ? { cta: this.config.cta } : {}),
        } satisfies ListColumn;

      case "checkbox":
        return {
          type: "checkbox",
          title: this.config.title,
          items: this.config.items || [],
          ...(this.config.placeholder
            ? {
                placeholder: this.config.placeholder,
              }
            : {}),
          ...(this.config.condition
            ? { condition: this.config.condition }
            : {}),
          ...(this.config.flex ? { flex: this.config.flex } : {}),
          ...(this.config.cta ? { cta: this.config.cta } : {}),
        } satisfies CheckboxColumn;

      case "search":
        return {
          type: "search",
          title: this.config.title,
          items: this.config.items || [],
          placeholder: this.config.placeholder || "",
          ...(this.config.allowCustomValue !== undefined
            ? {
                allowCustomValue: this.config.allowCustomValue,
              }
            : {}),
          ...(this.config.condition
            ? { condition: this.config.condition }
            : {}),
          ...(this.config.flex ? { flex: this.config.flex } : {}),
          ...(this.config.cta ? { cta: this.config.cta } : {}),
        } satisfies SearchColumn;

      case "enter-value":
        return {
          type: "enter-value",
          title: this.config.title,
          placeholder: this.config.placeholder || "",
          ...(this.config.condition
            ? { condition: this.config.condition }
            : {}),
          ...(this.config.flex ? { flex: this.config.flex } : {}),
          ...(this.config.cta ? { cta: this.config.cta } : {}),
        } satisfies EnterValueColumn;

      default:
        throw new Error(`Unknown column type: ${this.config.type}`);
    }
  }
}

// Simple column factory functions
export const column = {
  list: (title: string, items: Item[]) =>
    new ColumnBuilder("list", title, items),
  checkbox: (title: string, items: Item[]) =>
    new ColumnBuilder("checkbox", title, items),
  search: (title: string, items: Item[]) =>
    new ColumnBuilder("search", title, items),
  enterValue: (title: string, placeholder: string) =>
    new ColumnBuilder("enter-value", title, undefined, placeholder),
};

// Path-aware dropdown configuration builder
export class PathAwareDropdownBuilder {
  private criteriaOptions: Item[] = [];
  private pathConfigs = new Map<string, Column[][]>();
  private firstColumnCTA?: React.ReactNode;
  private firstColumnTitle = "Select Criteria";

  /**
   * Define the first column criteria options
   */
  criteria(items: Item[], title?: string): PathAwareDropdownBuilder {
    this.criteriaOptions = items;
    if (title) {
      this.firstColumnTitle = title;
    }
    return this;
  }

  /**
   * Define configuration for a specific path (first column selection)
   * @param criteriaId - The ID of the first column selection
   * @param configFn - Function that returns column configurations
   */
  path(
    criteriaId: string,
    configFn: (builder: PathColumnBuilder) => PathColumnBuilder,
  ): PathAwareDropdownBuilder {
    const pathBuilder = new PathColumnBuilder(criteriaId);
    configFn(pathBuilder);
    this.pathConfigs.set(criteriaId, pathBuilder.getColumns());
    return this;
  }

  /**
   * Build the final configuration by merging all paths
   */
  build(): SteppedMultiColumnDropdownConfig {
    const config: SteppedMultiColumnDropdownConfig = [];

    // First column: criteria selection
    const firstColumn = column
      .list(this.firstColumnTitle, this.criteriaOptions)
      .withFlex(true);
    if (this.firstColumnCTA) {
      firstColumn.withCTA(this.firstColumnCTA);
    }
    config.push([firstColumn.build()]);

    // Find the maximum number of columns across all paths
    let maxColumns = 0;
    this.pathConfigs.forEach((pathConfig) => {
      maxColumns = Math.max(maxColumns, pathConfig.length);
    });

    // Merge columns from all paths
    for (let colIndex = 0; colIndex < maxColumns; colIndex++) {
      const columnConfigs: Column[] = [];

      this.pathConfigs.forEach((pathConfig, criteriaId) => {
        if (pathConfig[colIndex]) {
          // Check if this is the last column for this path
          const isLastColumn = colIndex === pathConfig.length - 1;

          // Add automatic condition for the first column selection
          pathConfig[colIndex].forEach((colConfig) => {
            // Create a new config object to avoid mutating the original
            const newConfig = { ...colConfig };

            // Automatically add flex to the last column of the path
            if (isLastColumn) {
              newConfig.flex = true;
            }

            // If no condition is set, add the default path condition
            if (!newConfig.condition) {
              newConfig.condition = conditions.equals(0, criteriaId);
            } else {
              // If a condition exists, combine it with the path condition
              const originalCondition = newConfig.condition;
              newConfig.condition = conditions.and(
                conditions.equals(0, criteriaId),
                originalCondition,
              );
            }
            columnConfigs.push(newConfig);
          });
        }
      });

      if (columnConfigs.length > 0) {
        config.push(columnConfigs);
      }
    }

    return config;
  }
}

// Path-specific column builder
export class PathColumnBuilder {
  private columns: Column[][] = [];
  private criteriaId: string;

  constructor(criteriaId: string) {
    this.criteriaId = criteriaId;
  }

  /**
   * Add a column to this path
   */
  column(...configs: (Column | ColumnBuilder)[]): PathColumnBuilder {
    const processedConfigs = configs.map((config) =>
      config instanceof ColumnBuilder ? config.build() : config,
    );
    this.columns.push(processedConfigs);
    return this;
  }

  getColumns(): Column[][] {
    return this.columns;
  }
}

/**
 * Create a path-aware dropdown configuration builder
 */
export function createPathAwareDropdown(): PathAwareDropdownBuilder {
  return new PathAwareDropdownBuilder();
}

/**
 * Helper to create operator column for a path
 */
export function pathOperators(
  operators: Item[],
  additionalCondition?: ConditionFn,
): ColumnBuilder {
  const builder = column.list("Select Operator", operators);
  if (additionalCondition) {
    builder.withCondition(additionalCondition);
  }
  return builder;
}
